import React from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";

import ACCESS_LEVELS, { ADMINS, STUDENT } from "../config/accessLevels";

// actions
import { useScriptConverter } from "../utils/languageHelpers";

const Authorization = ({ component: AuthComponent, level }, ...props) => {
  const isLogin = useSelector((state) => state.auth.isLogin);
  const roles = useSelector((state) => state.auth.user?.roles);
  const canEdit = useSelector((state) => state.auth.user?.canEdit); // NOTE: ID of canEdit array is product ID!
  const converter = useScriptConverter();
  const location = useLocation();

  const check = ({ isLogin, roles }) => {
    if (!isLogin) {
      // Store the original path for redirect after login
      const pathToStore = location.pathname + location.search;
      console.log("[Authorization] Not logged in, storing path:", pathToStore);
      sessionStorage.setItem("previousURL", pathToStore);

      // Extract and store isTablet parameter if present
      const searchParams = new URLSearchParams(location.search);
      const pathSegments = location.pathname.split("/");
      const lastSegment = pathSegments[pathSegments.length - 1];

      const isTabletFromQuery = searchParams.get("isTablet") === "true";
      const isTabletFromPath = lastSegment.includes("isTablet=true");

      if (isTabletFromQuery || isTabletFromPath) {
        console.log(
          "[Authorization] Detected isTablet=true, storing in sessionStorage"
        );
        sessionStorage.setItem("isTablet_teachingAssistant_student", "true");
      }

      // Extract and store filter parameter if present
      const filterFromQuery = searchParams.get("filter");
      let filterFromPath = null;
      if (lastSegment.includes("filter=")) {
        const filterMatch = lastSegment.match(/filter=([^&/]+)/);
        if (filterMatch) {
          filterFromPath = decodeURIComponent(filterMatch[1]);
        }
      }

      const filterValue = filterFromQuery || filterFromPath;
      if (filterValue) {
        console.log(
          `[Authorization] Detected filter=${filterValue}, storing in sessionStorage`
        );
        sessionStorage.setItem(
          "booksetFilter_teachingAssistant_student",
          filterValue
        );
      }

      return { authorized: false, redirectTo: "/" };
    }

    // User has no roles at all - redirect to home with error
    if (!Array.isArray(roles) || roles.length === 0) {
      console.log("[Authorization] User has no roles, redirecting to home");
      alert(
        converter({
          en: "Identity error! \nPlease check invitation code with your principal or Melody consultants,Or contact the Academic Affairs Office and engineers.",
          tw: "身分認證錯誤! \n請洽詢您的校長索取登入邀請碼,或是聯繫教務及工程師",
          cn: "身分认证错误! \n请洽询您的校长索取登入邀请码,或是联繫教务及工程师"
        })
      );
      return { authorized: false, redirectTo: "/" };
    }

    if (
      (level !== undefined &&
        roles.some((role) => ACCESS_LEVELS[level].indexOf(role) !== -1)) ||
      (Array.isArray(canEdit) && canEdit.length > 0)
    ) {
      return { authorized: true };
    } else {
      // User is logged in but doesn't have permission for this route
      // Redirect to the same path but with the correct role segment
      const userRoleRoute = roles.some((role) =>
        ACCESS_LEVELS[ADMINS].includes(role)
      )
        ? "admin"
        : roles.some((role) => ACCESS_LEVELS[STUDENT].includes(role))
          ? "student"
          : "teacher";

      console.log(
        "[Authorization] User logged in but wrong role. userRoleRoute:",
        userRoleRoute
      );

      const pathSegments = location.pathname.split("/").filter(Boolean);
      console.log("[Authorization] pathSegments:", pathSegments);

      if (pathSegments.length > 0) {
        const firstSegment = pathSegments[0];
        console.log("[Authorization] firstSegment:", firstSegment);

        // Check if first segment is a role (admin/teacher/student)
        if (
          firstSegment === "admin" ||
          firstSegment === "teacher" ||
          firstSegment === "student"
        ) {
          // Replace with user's actual role
          pathSegments[0] = userRoleRoute;
          const redirectPath = "/" + pathSegments.join("/") + location.search;
          console.log("[Authorization] Redirecting to:", redirectPath);
          return { authorized: false, redirectTo: redirectPath };
        }
      }

      // Show error and redirect to home if path doesn't match expected format
      alert(
        converter({
          en: "Identity error! \nPlease check invitation code with your principal or Melody consultants,Or contact the Academic Affairs Office and engineers.",
          tw: "身分認證錯誤! \n請洽詢您的校長索取登入邀請碼,或是聯繫教務及工程師",
          cn: "身分认证错误! \n请洽询您的校长索取登入邀请码,或是联繫教务及工程师"
        })
      );
      return { authorized: false, redirectTo: "/" };
    }
  };

  const result = check({ isLogin, roles });

  return result.authorized ? (
    <AuthComponent {...props} />
  ) : (
    <Navigate to={result.redirectTo} replace />
  );
};
export default Authorization;
